import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { flushSync } from 'react-dom';
import { Grid, Tab, Tabs, Box } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useVehicleContext } from '../../context/vehicleContext';
import { getCountryDisplay } from '../../utils/countryUtils';
import { PotentialMatchesTable } from '../../components/tables/potentialMatchesTable';
import { FilterKey, PotentialOffer, TruckDetails } from '../../types';
import { useLocalStorage, useAutoRefresh, useAnalytics, useEngagementTracking, useTenant } from '../../hooks';
import { toast } from '../../utils/toast';
import { useTranslation } from 'react-i18next';
import useConfirm from '../../hooks/ui/useConfirm';
import { Considerations, defaultConsiderations } from '../../models/considerations';
import { PotentialOffersFilter } from '../../models/potentialOffersFilter';
import { VehicleAutoSearch, SearchFilterMode, mapAutoSearchModeToFilterMode, mapFilterModeToAutoSearchMode } from '../../models/vehicleAutoSearch';
import { VehicleAutoNegoSession } from '../../models/vehicleAutoNegoSession';
import { fetchVehicleOffers } from '../../services/vehicleService';
import { getAutoSessions, startAutoSearch, endAutoSearch } from '../../services/autoSessionService';
import type { GroupageTrip, GroupageTripWithNegotiations } from '../../models/groupageTrip';
import { getActiveGroupageTrip, getGroupageTrip, createGroupageTrip, addNegotiationToTrip, listGroupageTrips } from '../../services/groupageTripService';
import GroupageTripDialog from '../../components/groupage/GroupageTripDialog';
import useCollapse from '../../hooks/ui/useCollapse';
import OfferSearchTable from '../../components/tables/searchTable/offerSearchTable';
import FleetTable from '../../components/tables/fleetTable/fleetTable';
import { OnboardingChecklist } from '../../components/onboarding';
import { AutomationStatusOverview, NeedsHumanQueue } from '../../components/automation';
import { PushNotificationPrompt, usePushNotificationPrompt } from '../../components/notifications';

export type PotentialOffersOrderBy = 'estimatedPrice' | 'finalScore' | 'latestStart' | 'creationDateTime' | undefined;

/** Load Search page - displays fleet vehicles and their potential offers. */
const ActiveTrucks: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { vehicles, fetchVehicles } = useVehicleContext();
  // Analytics tracking
  const { trackFeatureUsage } = useAnalytics();
  useEngagementTracking('load_search');

  const [tabIndex, setTabIndex] = useState<number>(0);

  // Push notification prompt
  const { showPrompt, triggerPrompt, dismissPrompt, shouldShowPrompt } = usePushNotificationPrompt();

  // Truck selection (ID only - TruckDetails derived from vehicles)
  const [selectedTruckId, setSelectedTruckId] = useState<number | null>(() => {
    const savedTruckId = localStorage.getItem('selectedTruckId');
    return savedTruckId ? Number(savedTruckId) : null;
  });

  // Tenant data
  const { tenant: tenantData } = useTenant();

  // Potential offers state
  const [offers, setOffers] = useState<PotentialOffer[]>([]);
  const [vehicleOffersLoading, setVehicleOffersLoading] = useState<boolean>(false);
  const [offersLoadError, setOffersLoadError] = useState<boolean>(false);
  const [totalOffers, setTotalOffers] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const pageSize = 15;
  const [potentialOrderColumn, setPotentialOrderColumn] = useLocalStorage<PotentialOffersOrderBy>('potentialOrderColumn', 'finalScore');
  const [potentialOrder, setPotentialOrder] = useLocalStorage<'ASC' | 'DESC' | undefined>('potentialOrder', 'DESC');

  // Ongoing offers state
  const [ongoingOffers, setOngoingOffers] = useState<PotentialOffer[]>([]);
  const [ongoingOfferLoading, setOngoingOfferLoading] = useState<boolean>(false);
  const [ongoingLoadError, setOngoingLoadError] = useState<boolean>(false);
  const [totalOngoingOffers, setTotalOngoingOffers] = useState<number>(0);
  const [ongoingPage, setOngoingPage] = useState<number>(1);
  const [ongoingOrderColumn, setOngoingOrderColumn] = useLocalStorage<PotentialOffersOrderBy>('ongoingOrderColumn', undefined);
  const [ongoingOrder, setOngoingOrder] = useLocalStorage<'ASC' | 'DESC' | undefined>('ongoingOrder', undefined);
  const [ongoingStats, setOngoingStats] = useState<{ unreadEmails: number, needHuman: number, waitingForClient: number } | undefined>(undefined);

  // Filter state (for potential offers table)
  const [considerations, setConsiderations] = useLocalStorage<Considerations>('offerConsiderations', defaultConsiderations);
  const [filterMode, setFilterMode] = useLocalStorage<SearchFilterMode>('offerFilterMode', 'default');
  const [selectedCountries, setSelectedCountries] = useLocalStorage<string[]>('offerSelectedCountries', []);
  const [filters, setFilters] = useLocalStorage<PotentialOffersFilter>('offerFilters', {});
  const [useAutoSearchSync, setUseAutoSearchSync] = useState<boolean>(true);

  // Auto sessions state
  const [autoSearchSessions, setAutoSearchSessions] = useState<Map<number, VehicleAutoSearch | null>>(new Map());
  const [autoNegoSessions, setAutoNegoSessions] = useState<Map<number, VehicleAutoNegoSession | null>>(new Map());
  const [autoSessionsLoading, setAutoSessionsLoading] = useState<Set<number>>(new Set());
  const [autoSessionsHealthy, setAutoSessionsHealthy] = useState<boolean>(true);

  // Groupage trip state
  const [activeGroupageTrip, setActiveGroupageTrip] = useState<GroupageTrip | null>(null);
  const [groupageTripDialogOpen, setGroupageTripDialogOpen] = useState(false);
  const [groupageTripDialogId, setGroupageTripDialogId] = useState<number | null>(null);

  const groupageTripNegoIds = useMemo(() => {
    const negoTrip = activeGroupageTrip as GroupageTripWithNegotiations | null;
    if (!negoTrip?.negotiations) return undefined;
    return new Set(negoTrip.negotiations.map(n => n.id));
  }, [activeGroupageTrip]);

  const {
    confirm: confirmGroupage,
    setConfirmTitle: setGroupageConfirmTitle,
    setConfirmDescription: setGroupageConfirmDesc,
    setConfirmText: setGroupageConfirmText,
    setConfirmIsOpen: setGroupageConfirmOpen,
    ConfirmModal: GroupageConfirmModal,
  } = useConfirm();

  const [negotiationToGroupageTripMap, setNegotiationToGroupageTripMap] = useState<Map<string, number> | undefined>(undefined);

  // One-click AI toggle state (shared across all trucks)
  const [oneClickEnabled, setOneClickEnabled] = useLocalStorage<boolean>('ai-one-click-enabled', true);
  const [oneClickServices, setOneClickServices] = useLocalStorage<{ search: boolean; nego: boolean }>('ai-one-click-services', { search: true, nego: true });

  // Separate auto-refresh hooks for each table
  const { countDown, refreshTrigger, isInitialLoad, manualRefresh, resetInitialLoad } = useAutoRefresh({
    countdownSeconds: 60,
    enabled: !!selectedTruckId,
  });

  const { countDown: ongoingCountDown, refreshTrigger: ongoingRefreshTrigger, isInitialLoad: ongoingIsInitialLoad, manualRefresh: ongoingManualRefresh, resetInitialLoad: ongoingResetInitialLoad } = useAutoRefresh({
    countdownSeconds: 60,
    enabled: !!selectedTruckId,
  });

  const { CollapseButton, isCollapsed } = useCollapse('fleetTableCollapse', false);

  /** Offers removed locally after Close; filters stale rows until GET /offers catches up */
  const recentlyClosedOngoingOfferIdsRef = useRef<Set<number>>(new Set());

  const handleOngoingNegotiationClosed = useCallback((offer: PotentialOffer) => {
    flushSync(() => {
      recentlyClosedOngoingOfferIdsRef.current.add(offer.id);
      setOngoingOffers((prev) => prev.filter((o) => o.id !== offer.id));
      setTotalOngoingOffers((prev) => Math.max(0, prev - 1));
    });
  }, []);

  const handleOngoingNegotiationCloseFailed = useCallback((offer: PotentialOffer) => {
    recentlyClosedOngoingOfferIdsRef.current.delete(offer.id);
    ongoingManualRefresh();
  }, [ongoingManualRefresh]);

  useEffect(() => {
    fetchVehicles({ with_drivers: true, with_current_booking: true });
  }, [fetchVehicles]);

  const fetchAutoSessions = useCallback(async (): Promise<void> => {
    const vehicleIds = vehicles.map(v => v.id);
    if (vehicleIds.length === 0) return;

    setAutoSessionsLoading(new Set(vehicleIds));

    try {
      const { autoSearches, autoNegoSessions: negoSessions } = await getAutoSessions(vehicleIds);
      setAutoSearchSessions(new Map(Object.entries(autoSearches).map(([k, v]) => [Number(k), v])));
      setAutoNegoSessions(new Map(Object.entries(negoSessions).map(([k, v]) => [Number(k), v])));
      setAutoSessionsHealthy(true);
    } catch {
      console.error('Failed to fetch auto sessions');
      setAutoSessionsHealthy(false);
    } finally {
      setAutoSessionsLoading(new Set());
    }
  }, [vehicles]);

  useEffect(() => {
    if (vehicles.length > 0) fetchAutoSessions();
  }, [vehicles, fetchAutoSessions]);

  // Poll auto sessions every 30s
  useEffect(() => {
    if (vehicles.length === 0) return;
    const interval = setInterval(fetchAutoSessions, 30000);
    return () => clearInterval(interval);
  }, [fetchAutoSessions, vehicles.length]);

  const handleAutoSessionSuccess = useCallback((): void => {
    fetchAutoSessions();
    toast.success(t('Session updated successfully'));
  }, [fetchAutoSessions, t]);

  // /loadSearch Fleet table should show only trucks that already have a booking.
  const fleetVehicles = useMemo(() => vehicles.filter((v) => Boolean(v.current_booking_id || v.currentBooking?.id)), [vehicles]);

  /** Saves the current manual filter settings into the active auto-search session */
  const handleUpdateAutoSearch = useCallback(async (): Promise<void> => {
    if (!selectedTruckId) return;

    const currentSession = autoSearchSessions.get(selectedTruckId);
    const { auto_search_mode, should_return_reg_country } = mapFilterModeToAutoSearchMode(filterMode);

    try {
      if (currentSession) {
        await endAutoSearch(selectedTruckId, { cancel_negotiations: false, send_email: false });
      }

      // Drop loading-time bounds that are in the past (stale from a previous session) to avoid DATE_OUT_OF_RANGE from the backend.
      const nowMs = Date.now();
      const startMs = filters?.startTime instanceof Date ? filters.startTime.getTime() : undefined;
      const endMs = filters?.endTime instanceof Date ? filters.endTime.getTime() : undefined;

      await startAutoSearch(selectedTruckId, {
        auto_search_mode,
        should_return_reg_country,
        selected_countries: filterMode === 'selectedCountries'
          ? selectedCountries.filter((c) => c && c.trim() !== '')
          : undefined,
        full_truck_load: filters?.fullTruckLoad ?? false,
        start_loading_time: startMs !== undefined && startMs >= nowMs ? startMs : undefined,
        end_loading_time: endMs !== undefined && endMs >= nowMs ? endMs : undefined,
        after_booking_id: currentSession?.after_booking_id ?? undefined,
      });

      await fetchAutoSessions();
      toast.success(t('Auto-search updated with current filters'));
    } catch {
      toast.error(t('Failed to update auto-search with filters'));
    }
  }, [selectedTruckId, autoSearchSessions, filterMode, selectedCountries, filters, fetchAutoSessions, t]);

  // Clear selection if vehicle no longer exists
  useEffect(() => {
    if (selectedTruckId && vehicles.length > 0) {
      const vehicleExists = vehicles.some((v) => v.id === selectedTruckId);
      if (!vehicleExists) {
        localStorage.removeItem('selectedTruckId');
        setSelectedTruckId(null);
      }
    }
  }, [vehicles, selectedTruckId]);

  // Auto-select vehicle from URL param (e.g. when user clicks a push notification)
  useEffect(() => {
    if (vehicles.length === 0) return;
    const vehicleIdParam = searchParams.get('vehicleId');
    if (!vehicleIdParam) return;
    const vehicleId = Number(vehicleIdParam);
    if (isNaN(vehicleId) || vehicleId === selectedTruckId) return;
    if (vehicles.some((v) => v.id === vehicleId)) {
      handleTruckClick(vehicleId);
    }
  }, [vehicles, searchParams]);

  // Auto-search info for the selected truck — drives loading/empty/error UI in PotentialMatchesTable.
  // marketplaceOffersFound = sum across the latest history entry of every SearchRecord (one per active marketplace).
  const autoSearchInfo = useMemo(() => {
    if (!selectedTruckId) return null;
    const session = autoSearchSessions.get(selectedTruckId);
    if (!session) return null;
    const marketplaceOffersFound = (session.searches ?? []).reduce((sum, s) => {
      const last = s.history?.[s.history.length - 1];
      return sum + (last?.marketplaceOffersFound ?? 0);
    }, 0);
    return {
      state: session.autoSearchState,
      statusMessage: session.statusMessage,
      hasAnyExecution: (session.searches?.length ?? 0) > 0,
      marketplaceOffersFound,
    };
  }, [selectedTruckId, autoSearchSessions]);

  // Effective filter mode (auto-sync from auto-search session if enabled)
  const effectiveFilterMode = useMemo((): SearchFilterMode => {
    if (!useAutoSearchSync || !selectedTruckId) return filterMode;

    const currentAutoSearch = autoSearchSessions.get(selectedTruckId);
    if (!currentAutoSearch) return filterMode;

    return mapAutoSearchModeToFilterMode(currentAutoSearch);
  }, [useAutoSearchSync, selectedTruckId, autoSearchSessions, filterMode]);

  // Derived truck details
  const selectedTruck = useMemo((): TruckDetails | null => {
    if (!selectedTruckId) return null;

    const vehicle = vehicles.find((v) => v.id === selectedTruckId);
    if (!vehicle) return null;

    return {
      id: selectedTruckId.toString(),
      plate: vehicle.truckPlate || 'N/A',
      truckPlate: vehicle.truckPlate,
      trailerPlate: vehicle.trailerPlate,
      driver: vehicle.name || 'Unknown',
      location: vehicle.currentPosition
        ? `${getCountryDisplay(vehicle.currentPosition.country)}, ${vehicle.currentPosition.postalCode}`
        : 'Unknown',
      equipment: Array.isArray(vehicle.equipment) ? vehicle.equipment.join(', ') : vehicle.equipment || 'Standard',
      status: vehicle.isActive ? 'active' : 'inactive',
      matches: [],
    };
  }, [selectedTruckId, vehicles]);

  // Booking context for groupage trip scoping
  const afterBookingId = useMemo((): number | undefined => {
    if(!selectedTruckId) return undefined;
    const vehicle = vehicles.find(v => v.id === selectedTruckId);
    return vehicle?.current_booking_id;
  }, [selectedTruckId, vehicles]);

  const handleTruckClick = useCallback((vehicleId: number) => {
    if (vehicleId === selectedTruckId) return;

    trackFeatureUsage('load_search', 'truck_selected', { vehicle_id: vehicleId });

    setPage(1);
    setOngoingPage(1);

    // Reset initial load flags so skeleton shows for new truck
    resetInitialLoad();
    ongoingResetInitialLoad();

    // Immediately clear previous truck data and show loading feedback.
    setOffers([]);
    setTotalOffers(0);
    setOffersLoadError(false);
    setOngoingOffers([]);
    setTotalOngoingOffers(0);
    setOngoingStats(undefined);
    setOngoingLoadError(false);
    setVehicleOffersLoading(true);
    setOngoingOfferLoading(true);

    setSelectedTruckId(vehicleId);
    localStorage.setItem('selectedTruckId', vehicleId.toString());
  }, [selectedTruckId, resetInitialLoad, ongoingResetInitialLoad, trackFeatureUsage]);

  // Fetches the active trip with full negotiations (needed for groupageTripNegoIds)
  const fetchActiveGroupageTripFull = useCallback(async (vehicleId: number, bookingId?: number): Promise<void> => {
    const trip = await getActiveGroupageTrip(vehicleId, bookingId);
    if (!trip) { setActiveGroupageTrip(null); return; }
    const full = await getGroupageTrip(vehicleId, trip.id);
    setActiveGroupageTrip(full);
  }, []);

  const fetchAllGroupageTrips = useCallback(async (vehicleId: number): Promise<void> => {
    try {
      const trips = await listGroupageTrips(vehicleId);
      const map = new Map<string, number>();
      const results = await Promise.allSettled(
        trips.map(trip => getGroupageTrip(vehicleId, trip.id).then(fullTrip => ({tripId: trip.id, fullTrip})))
      );
      for (const result of results) {
        if (result.status === 'fulfilled') {
          const {tripId, fullTrip} = result.value;
          if (fullTrip.negotiations) {
            for (const nego of fullTrip.negotiations) {
              map.set(nego.id, tripId);
            }
          }
        }
      }
      setNegotiationToGroupageTripMap(map);
    } catch (_err) {
      // Error already toasted by service
      setNegotiationToGroupageTripMap(undefined);
    }
  }, []);

  // Fetch active groupage trip when vehicle or booking changes
  useEffect(() => {
    if (!selectedTruckId) {
      setActiveGroupageTrip(null);
      return;
    }
    fetchActiveGroupageTripFull(selectedTruckId, afterBookingId).catch(() => setActiveGroupageTrip(null));
  }, [selectedTruckId, afterBookingId, fetchActiveGroupageTripFull]);

  useEffect(() => {
    if (!selectedTruckId) {
      setNegotiationToGroupageTripMap(undefined);
      return;
    }
    fetchAllGroupageTrips(selectedTruckId);
  }, [selectedTruckId, fetchAllGroupageTrips]);

  const [groupagePendingOfferId, setGroupagePendingOfferId] = useState<number | null>(null);

  const handleCreateGroupageTrip = useCallback(async (offer: PotentialOffer): Promise<void> => {
    if (!selectedTruckId || groupagePendingOfferId !== null) return;
    const negotiationId = offer.negotiations?.[0]?.id;
    if (!negotiationId) return;
    setGroupagePendingOfferId(offer.id);
    try {
      const tripId = await createGroupageTrip(selectedTruckId, [negotiationId], afterBookingId);
      const trip = await getGroupageTrip(selectedTruckId, tripId);
      setActiveGroupageTrip(trip);
      setGroupageTripDialogId(tripId);
      setGroupageTripDialogOpen(true);
      fetchAllGroupageTrips(selectedTruckId);
    } catch(_err) {
      // error already toasted by service
    } finally {
      setGroupagePendingOfferId(null);
    }
  }, [selectedTruckId, afterBookingId, groupagePendingOfferId, fetchAllGroupageTrips]);

  const handleGroupageTripChanged = useCallback((): void => {
    if (!selectedTruckId) return;
    fetchActiveGroupageTripFull(selectedTruckId, afterBookingId).catch(() => setActiveGroupageTrip(null));
    fetchAllGroupageTrips(selectedTruckId);
  }, [selectedTruckId, afterBookingId, fetchActiveGroupageTripFull, fetchAllGroupageTrips]);

  const handleOpenOtherGroupageTrip = useCallback((tripId: number): void => {
    setGroupageTripDialogId(tripId);
    setGroupageTripDialogOpen(true);
  }, []);

  const handleAddToGroupageTrip = useCallback(async (offer: PotentialOffer): Promise<void> => {
    if (!selectedTruckId || !activeGroupageTrip || groupagePendingOfferId !== null) return;
    const negotiationId = offer.negotiations?.[0]?.id;
    if (!negotiationId) return;
    setGroupagePendingOfferId(offer.id);
    try {
      await addNegotiationToTrip(selectedTruckId, activeGroupageTrip.id, negotiationId);
      const trip = await getGroupageTrip(selectedTruckId, activeGroupageTrip.id);
      setActiveGroupageTrip(trip);
      setGroupageTripDialogId(activeGroupageTrip.id);
      setGroupageTripDialogOpen(true);
      fetchAllGroupageTrips(selectedTruckId);
    } catch(_err) {
      // error already toasted by service
    } finally {
      setGroupagePendingOfferId(null);
    }
  }, [selectedTruckId, activeGroupageTrip, groupagePendingOfferId, fetchAllGroupageTrips]);

  const [offerPattern, setOfferPattern] = useState<string | undefined>(undefined);

  // Fetch offers when selection or filters change
  useEffect(() => {
    if (!selectedTruckId) return;
    if (!effectiveFilterMode) return;

    setVehicleOffersLoading(true);
    setOffersLoadError(false);

    const loadOffers = async () => {
      try {
        const limit = pageSize;
        const offset = (page - 1) * limit;
        const { offers: fetchedOffers, count } = await fetchVehicleOffers(
          selectedTruckId,
          considerations,
          effectiveFilterMode,
          offset,
          limit,
          potentialOrderColumn,
          potentialOrder,
          'EXCLUDE',
          offerPattern,
          undefined,
          effectiveFilterMode === 'selectedCountries' ? selectedCountries : undefined,
          filters,
          activeGroupageTrip?.remainingWeightT,
          activeGroupageTrip?.remainingLengthM
        );

        setOffers(fetchedOffers);
        setTotalOffers(count);
        setOffersLoadError(false);
      } catch {
        setOffers([]);
        setOffersLoadError(true);
      } finally {
        setVehicleOffersLoading(false);
      }
    };

    const timer = setTimeout(loadOffers, 300);

    return () => clearTimeout(timer);
  }, [selectedTruckId, page, potentialOrder, potentialOrderColumn, considerations, effectiveFilterMode, selectedCountries, filters, refreshTrigger, tenantData, t, offerPattern, activeGroupageTrip]);

  // Fetch ongoing offers when selection or filters change
  // Note: Ongoing offers always use 'default' filter mode (independent of main table)
  useEffect(() => {
    if (!selectedTruckId) return;

    const loadOngoingOffers = async () => {
      setOngoingOfferLoading(true);
      setOngoingLoadError(false);
      try {
        const limit = pageSize;
        const offset = (ongoingPage - 1) * limit;
        const { offers: fetchedOffers, count, negotiationsStats } = await fetchVehicleOffers(
          selectedTruckId,
          considerations,
          'default', // Ongoing offers always use default filter mode
          offset,
          limit,
          ongoingOrderColumn,
          ongoingOrder,
          'ONLY',
          undefined,
          false
        );

        const staleClosed = recentlyClosedOngoingOfferIdsRef.current;
        [...staleClosed].forEach((id) => {
          if (!fetchedOffers.some((o) => o.id === id)) {
            staleClosed.delete(id);
          }
        });

        const filteredOffers = fetchedOffers.filter((o) => !staleClosed.has(o.id));
        const hiddenOnPage = fetchedOffers.length - filteredOffers.length;

        setOngoingOffers(filteredOffers);
        setTotalOngoingOffers(Math.max(0, count - hiddenOnPage));
        setOngoingStats(negotiationsStats);
        setOngoingLoadError(false);
      } catch {
        setOngoingOffers([]);
        setOngoingLoadError(true);
      } finally {
        setOngoingOfferLoading(false);
      }
    };

    loadOngoingOffers();
  }, [selectedTruckId, ongoingPage, ongoingOrder, ongoingOrderColumn, considerations, ongoingRefreshTrigger, tenantData, t]);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
    trackFeatureUsage('load_search', 'page_changed', { page: newPage, table: 'potential_matches' });
  }, [trackFeatureUsage]);

  const handleOngoingPageChange = useCallback((newPage: number) => {
    setOngoingPage(newPage);
    trackFeatureUsage('load_search', 'page_changed', { page: newPage, table: 'ongoing_offers' });
  }, [trackFeatureUsage]);

  const handleSortChange = useCallback((newOrderBy: PotentialOffersOrderBy) => {
    trackFeatureUsage('load_search', 'sort_changed', { column: newOrderBy || 'none', table: 'potential_matches' });
    // If clicking the same column, cycle through: DESC → ASC → Neutral
    if (potentialOrderColumn === newOrderBy) {
      if (potentialOrder === 'DESC') {
        setPotentialOrder('ASC');
      } else if (potentialOrder === 'ASC') {
        setPotentialOrder(undefined);
        setPotentialOrderColumn(undefined);
      }
    } else {
      // If clicking a different column, start with DESC
      setPotentialOrderColumn(newOrderBy);
      setPotentialOrder('DESC');
    }
  }, [potentialOrderColumn, potentialOrder, trackFeatureUsage]);

  const handleOngoingOfferSortChange = useCallback((newOrderBy: PotentialOffersOrderBy) => {
    trackFeatureUsage('load_search', 'sort_changed', { column: newOrderBy || 'none', table: 'ongoing_offers' });
    // If clicking the same column, cycle through: DESC → ASC → Neutral
    if (ongoingOrderColumn === newOrderBy) {
      if (ongoingOrder === 'DESC') {
        setOngoingOrder('ASC');
      } else if (ongoingOrder === 'ASC') {
        setOngoingOrder(undefined);
        setOngoingOrderColumn(undefined);
      }
    } else {
      // If clicking a different column, start with DESC
      setOngoingOrderColumn(newOrderBy);
      setOngoingOrder('DESC');
    }
  }, [ongoingOrderColumn, ongoingOrder, trackFeatureUsage]);

  // Trigger push notification prompt when user first lands (after small delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (shouldShowPrompt()) {
        triggerPrompt();
      }
    }, 3000); // Show after 3 seconds
    return () => clearTimeout(timer);
  }, []);

  // Handlers for onboarding checklist
  const handleOpenSettings = (tab?: number) => {
    trackFeatureUsage('load_search', 'open_settings', { tab: tab ?? 0 });
    // Access parent sidebar ref to open settings
    // This would need to be passed down from layout
    window.dispatchEvent(new CustomEvent('openTenantSettings', { detail: { tab } }));
  };

  const handleOpenVehicleImport = () => {
    trackFeatureUsage('load_search', 'open_vehicle_import');
    // Navigate to monitoring with import modal open
    navigate('/monitoring?import=true');
  };

  const handleReviewNegotiation = (vehicleId: number) => {
    trackFeatureUsage('load_search', 'review_negotiation', { vehicle_id: vehicleId });
    // Select the vehicle and potentially open negotiation dialog
    handleTruckClick(vehicleId);
  };

  return (
    <>
      {/* Push Notification Prompt */}
      <PushNotificationPrompt
        show={showPrompt}
        onDismiss={dismissPrompt}
        message={t('Get notified instantly when AI finds great loads or needs your approval to close deals.')}
      />

      {/* Onboarding Checklist - shows for users who haven't completed setup */}
      <OnboardingChecklist
        tenant={tenantData}
        vehicles={vehicles}
        onOpenSettings={handleOpenSettings}
        onOpenVehicleImport={handleOpenVehicleImport}
        onNavigateToLoadSearch={() => { }}
        compact={true}
      />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'flex-start' }}>
        <Tabs value={tabIndex} onChange={(event, newValue) => {
          setTabIndex(newValue);
          trackFeatureUsage('load_search', 'tab_changed', { tab: newValue === 0 ? 'fleet' : 'offer_search' });
        }}>
          <Tab label={t('Fleet')} />
          <Tab label={t('Offer Search')} />
        </Tabs>

        <Box sx={{ display: 'flex', gap: 2, flexGrow: 1, justifyContent: 'flex-end', flexWrap: 'wrap', alignItems: 'flex-start' }}>
          {/* Automation Status Overview — only counts trucks with bookings (matches Fleet table) */}
          <AutomationStatusOverview
            vehicles={fleetVehicles}
            autoSearchSessions={autoSearchSessions}
            autoNegoSessions={autoNegoSessions}
          />

          {/* Needs Human Queue - shows items requiring user attention */}
          <NeedsHumanQueue
            vehicles={vehicles}
            autoNegoSessions={autoNegoSessions}
            onRefresh={fetchAutoSessions}
            onReviewNegotiation={handleReviewNegotiation}
            loading={autoSessionsLoading.size > 0}
            compact={true}
          />
        </Box>
      </Box>

      {/* Fleet tab */}
      {tabIndex === 0 && (
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: isCollapsed ? 2 : 3 }} sx={{ height: '100%' }} className="grid-animated">
            <FleetTable
              vehicles={fleetVehicles}
              selectedTruck={selectedTruck}
              autoSearchSessions={autoSearchSessions}
              autoNegoSessions={autoNegoSessions}
              loadingSessions={autoSessionsLoading}
              autoSessionsHealthy={autoSessionsHealthy}
              isCollapsed={isCollapsed}
              CollapseButton={CollapseButton}
              onTruckClick={handleTruckClick}
              onAutoSessionSuccess={handleAutoSessionSuccess}
              oneClickEnabled={oneClickEnabled}
              setOneClickEnabled={setOneClickEnabled}
              oneClickServices={oneClickServices}
              setOneClickServices={setOneClickServices}
              activeGroupageTrips={activeGroupageTrip && selectedTruckId ? new Map([[selectedTruckId, activeGroupageTrip]]) : undefined}
              onGroupageTripBadgeClick={(_vehicleId, tripId) => { setGroupageTripDialogId(tripId); setGroupageTripDialogOpen(true); }}
            />
          </Grid>

          <Grid size={{ xs: 12, md: isCollapsed ? 10 : 9 }} className="grid-animated">
            <PotentialMatchesTable
              mode="all"
              title={t('Potential Matches')}
              selectedTruck={selectedTruck}
              offers={offers}
              tenantData={tenantData}
              totalOffers={totalOffers}
              vehicleOffersLoading={vehicleOffersLoading}
              isInitialLoad={isInitialLoad}
              loadError={offersLoadError}
              page={page}
              pageSize={pageSize}
              orderBy={potentialOrderColumn}
              order={potentialOrder}
              considerations={considerations}
              filterMode={effectiveFilterMode}
              autoSearchSession={selectedTruck ? autoSearchSessions.get(Number(selectedTruck.id)) || null : null}
              autoSearchInfo={autoSearchInfo}
              onAutoSearchUpdate={fetchAutoSessions}
              useAutoSearchSync={useAutoSearchSync}
              selectedCountries={selectedCountries}
              filters={filters}
              hiddenFilters={new Set<FilterKey>(['status', 'originCountry', 'destinationCountry', 'contact'])}
              offerPattern={offerPattern}
              refreshCountdown={countDown}
              onPageChange={handlePageChange}
              onSortChange={handleSortChange}
              onConsiderationsChange={setConsiderations}
              onFilterModeChange={setFilterMode}
              onUseAutoSearchSyncChange={setUseAutoSearchSync}
              onSelectedCountriesChange={setSelectedCountries}
              onFiltersChange={setFilters}
              onUpdateAutoSearch={selectedTruckId && autoSearchSessions.get(selectedTruckId) ? handleUpdateAutoSearch : undefined}
              onOfferPatternChange={(s) => setOfferPattern(s === '' ? undefined : s)}
              onRefresh={manualRefresh}
              onNegotiationStarted={(offer: PotentialOffer, negotiationId: string) => {
                ongoingManualRefresh();
                if(selectedTruckId) fetchActiveGroupageTripFull(selectedTruckId, afterBookingId).catch(() => {});

                const trip = activeGroupageTrip;
                // eslint-disable-next-line eqeqeq
                if(trip && trip.remainingWeightT != null && trip.remainingLengthM != null && selectedTruckId) {
                  const w = offer.weightT ?? 0;
                  const l = offer.lengthM ?? 0;
                  if(w <= trip.remainingWeightT && l <= trip.remainingLengthM) {
                    setGroupageConfirmTitle(t('Add to Groupage Trip'));
                    setGroupageConfirmDesc(
                      t('Add this negotiation to groupage trip #{{id}}? Remaining capacity: {{w}}T / {{l}}m.', {
                        id: trip.id,
                        w: trip.remainingWeightT.toFixed(1),
                        l: trip.remainingLengthM.toFixed(1),
                      })
                    );
                    setGroupageConfirmText(t('Add'));
                    confirmGroupage().then((confirmed) => {
                      if(confirmed && selectedTruckId) {
                        addNegotiationToTrip(selectedTruckId, trip.id, negotiationId)
                          .then((updatedTrip) => {
                            setActiveGroupageTrip(updatedTrip);
                            setGroupageTripDialogOpen(true);
                            setGroupageTripDialogId(trip.id);
                            fetchAllGroupageTrips(selectedTruckId);
                          })
                          .catch(() => {})
                          .finally(() => setGroupageConfirmOpen(false));
                      }
                    });
                  }
                }
              }}
              groupageBannerLabel={activeGroupageTrip ? t('Groupage active — remaining capacity: {{w}}T / {{l}}m', {w: activeGroupageTrip.remainingWeightT.toFixed(1), l: activeGroupageTrip.remainingLengthM.toFixed(1)}) : undefined}
              onGroupageBannerClick={activeGroupageTrip ? () => { setGroupageTripDialogId(activeGroupageTrip.id); setGroupageTripDialogOpen(true); } : undefined}
            />

            <Box sx={{ mt: 2 }}>
              <PotentialMatchesTable
                mode="ongoing"
                title={t('Ongoing Offers')}
                selectedTruck={selectedTruck}
                offers={ongoingOffers}
                tenantData={tenantData}
                totalOffers={totalOngoingOffers}
                stats={ongoingStats}
                vehicleOffersLoading={ongoingOfferLoading}
                isInitialLoad={ongoingIsInitialLoad}
                loadError={ongoingLoadError}
                page={ongoingPage}
                pageSize={pageSize}
                orderBy={ongoingOrderColumn}
                order={ongoingOrder}
                considerations={considerations}
                filterMode={'default'}
                hiddenFilters={new Set<FilterKey>(['autoSearchSync', 'status', 'originCountry', 'destinationCountry', 'contact'])}
                autoSearchSession={null}
                useAutoSearchSync={false}
                refreshCountdown={ongoingCountDown}
                onPageChange={handleOngoingPageChange}
                onSortChange={handleOngoingOfferSortChange}
                onConsiderationsChange={setConsiderations}
                onRefresh={ongoingManualRefresh}
                onCreateGroupageTrip={!activeGroupageTrip ? handleCreateGroupageTrip : undefined}
                onAddToGroupageTrip={activeGroupageTrip ? handleAddToGroupageTrip : undefined}
                groupagePendingOfferId={groupagePendingOfferId}
                groupageTripNegotiationIds={groupageTripNegoIds}
                groupageRemainingWeightT={activeGroupageTrip?.remainingWeightT}
                groupageRemainingLengthM={activeGroupageTrip?.remainingLengthM}
                currentGroupageTripId={activeGroupageTrip?.id}
                negotiationToGroupageTripMap={negotiationToGroupageTripMap}
                onOpenOtherGroupageTrip={handleOpenOtherGroupageTrip}
                onNegotiationClosed={handleOngoingNegotiationClosed}
                onNegotiationCloseFailed={handleOngoingNegotiationCloseFailed}
              />
            </Box>
          </Grid>
        </Grid>
      )}

      {groupageTripDialogOpen && groupageTripDialogId !== null && selectedTruckId !== null && (
        <GroupageTripDialog
          open={groupageTripDialogOpen}
          onClose={() => setGroupageTripDialogOpen(false)}
          vehicleId={selectedTruckId}
          tripId={groupageTripDialogId}
          onFindMoreCargo={() => {
            setGroupageTripDialogOpen(false);
            // Refresh trip data (capacity might have changed) then refresh offers with new capacity filters
            handleGroupageTripChanged();
            manualRefresh();
          }}
          onTripChanged={handleGroupageTripChanged}
        />
      )}

      {GroupageConfirmModal}

      {/* Offer Search Table */}
      {tabIndex === 1 && <OfferSearchTable
        selectedTruck={selectedTruck}
        vehicles={vehicles}
        enabledMarketplaces={tenantData?.enabledMarketplaces}
      />}
    </>
  );
};

export default ActiveTrucks;
